import CardHeader from "@/components/Includes/CardHeader/CardHeader.vue";
import TableEmpty from '@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue';
import ITableCol from "@/interfaces/ITableCol";
import ITicket from "@/interfaces/ITicket";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import capitalizeFirstLetter from "@/libs/capitalizeFirstLetter";
import cleanData from "@/libs/cleanData";
import cloneObject from "@/libs/cloneObject";
import transformDateFields from "@/libs/date/transformDateFields";
import getParamQuery from "@/libs/getParamQuery";
import getQueryData from "@/libs/getQueryData";
import getTableColumns from "@/libs/getTableColumns";
import searchByTable from "@/libs/searchQuery/searchByTable";
import Multiselect from 'vue-multiselect';
import {Component, Vue, Watch} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';

import DefaultTools from '@/components/Includes/DefaultTools/DefaultTools.vue';
import Loader from '@/components/Includes/Loader/Loader.vue';
import Pagination from '@/components/Includes/Pagination/Pagination.vue';
import IPayloadQuery from '@/interfaces/IPayloadQuery';
import {Switch as cSwitch} from '@coreui/vue';

import TicketDetail from '@/components/Tickets/TicketDetail/TicketDetail.vue';
import IClientApp from '@/interfaces/IClientApp';
import ISelectItemDefault from '@/interfaces/selects/ISelectItemDefault';

@Component({
    name: 'TicketsList',
    components: {
        DefaultTools,
        Loader,
        Pagination,
        TicketDetail,
        TableEmpty,
        Multiselect,
        CardHeader,
        cSwitch
    }
})
export default class TicketsList extends Vue {

    // Begin Vuex Logic for Tickets
    @Action private ticketsList!: (query: IPayloadQuery) => Promise<void>;
    @Action private clientAppsDetail!: (id: number) => Promise<IClientApp>
    @Getter private getTickets!: () => ITicket[];
    // End Vuex Logic for Tickets

    private loading: boolean = false;

    private detailTicketForm: boolean = false;

    private tickets: ITicket[] = [];
    private clientApps: {[id: string]: IClientApp | Promise<IClientApp> | undefined} = {};

    private selectedTicket: ITicket | null = null;

    private filtered: any = null;

    private selectedTableColumns: IMultiSelectItem[] = [
        {
            label: 'issue',
            value: 'issue'
        }
    ];

    // Begin Config for Pagination
    private totalTickets: number | void | null = null;
    private currentPage: number = 1;
    private selectedLimit: string = '100';
    private selectedType = '';
    // End Config for Pagination


    // Begin Config for Table
    private showModalDetail(ticket: ITicket) {
        this.detailTicketForm = !this.detailTicketForm;
        this.selectedTicket = cloneObject(ticket);
    }

    private hideModalDetail() {
        this.detailTicketForm = false;
        this.selectedTicket = null;
    }

    private filterData(query: string) { // Method for search tool
        this.filtered = searchByTable(query, this.tickets)
    }

    private get typeOptions(): ISelectItemDefault[]  {
        return [
            {
                text: '',
                value: null,
            },
            {
                text: 'Connection Issues',
                value: 'CONNECTION_ISSUE',
            },
            {
                text: 'Finance & billing',
                value: 'FINANCE_AND_BILLING',
            },
            {
                text: 'Other topics',
                value: 'OTHER',
            }
        ];
    }

    private get tableItems(): ITicket[] {
        return this.filtered ? this.filtered : this.tickets;
    }

    private get tableColumns() {
        const [item = {}] = this.tableItems;

        return Object.keys(item).map(key => ({
            key,
            sortable: ['id', 'issue', 'systemInfo', 'deviceId', 'email'].includes(key),
        }));
    };

    private get fields() {
        if (this.tableColumns) {
            const columnsIds = this.selectedTableColumns.map((el: IMultiSelectItem) => el.value);
            return this.tableColumns.filter((el: any) => columnsIds.includes(el.key));
        } else {
            return this.tableColumns;
        }
    }

    // End Config for Table


    // Begin Query Logic
    private get payloadQuery(): IPayloadQuery {
        return {
            ...getParamQuery(this.$route),
            metaData: true
        }
    }

    private queryValue() {
        if (this.payloadQuery.query && this.payloadQuery.query.length > 0) {
            this.readQueryValue();
        } else {
            this.writeQueryValue();
            this.readQueryValue();
        }
    }

    private readQueryValue() {
        const queryData = getQueryData(this.$route.query);

        queryData.map(({key, value}) => {
            switch (key) {
                case 'page': {
                    this.currentPage = value;
                    break;
                }
                case 'limit': {
                    this.selectedLimit = value;
                    break;
                }
                case 'type': {
                    this.selectedType = value;
                    break;
                }
                default:
                    break;
            }
        });
    }

    @Watch('selectedLimit')
    @Watch('currentPage')
    @Watch('selectedType')
    private writeQueryValue() {
        const queryData: any = {
            page: this.currentPage,
            limit: this.selectedLimit,
            type: this.selectedType
        };

        this.$router.replace({
            name: this.$route.name || undefined,
            query: cleanData(queryData)
        });
    }

    // End Query Logic

    // Begin Transform Data for Form Campaign
    private checkDateFields() {
        const dateFields: string[] = ['createAt', 'updatedAt'];
        transformDateFields(dateFields, this.tickets)
    }

    // End Transform Data for Form Campaign

    @Watch('selectedLimit')
    @Watch('currentPage')
    @Watch('selectedType')
    private async getTicketsList() {
        if (this.loading) {
            return;
        }

        try {
            this.loading = true;
            this.queryValue();

            this.totalTickets = await this.ticketsList(this.payloadQuery);
            this.tickets = this.getTickets();

            this.checkDateFields();

            
        } catch (error) {
            throw new Error(error);
        }

        setTimeout(async () => {
            await this.$nextTick();
            this.loading = false;
        }, 300);
    }

    @Watch('tickets', {immediate: true})
    private onChangeTickets() {
        this.tickets.forEach(ticket => {
            const clientAppId = ticket.clientAppId;
            if (!this.clientApps[clientAppId]) {
                const promise = this.clientAppsDetail(clientAppId).then(clientApp => {
                    if (clientApp.id) {
                        this.$set(this.clientApps, clientApp.id, clientApp);
                    }
                });

                this.$set(this.clientApps, clientAppId, promise);
            }
        })
    }

    private getClientApp(id: number) {
        const clientApp = this.clientApps[id];
        if (clientApp){
            if(!(clientApp instanceof Promise)) {
                return  clientApp;
            }
        }

        return '';
    }

    private mounted() {
        this.getTicketsList();
    }
}
